import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import {
  LayoutDashboard,
  Users,
  Truck,
  ShoppingCart,
  Factory,
  FileText,
  Package,
  PieChart,
  Settings,
  UserCog,
  History,
  LogOut,
  DollarSign,
  UserCheck,
  ClipboardCheck,
  Calculator,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Boxes
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_4582f8f6-2ad3-47d2-8ff9-2570dde86b39/artifacts/agvwhrdp_Logo.png";

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', module: 'dashboard' },
  { path: '/suppliers', icon: Truck, label: 'Suppliers', module: 'suppliers' },
  { path: '/customers', icon: Users, label: 'Customers', module: 'customers' },
  { path: '/agents', icon: UserCheck, label: 'Sales Agents', module: 'agents' },
  { path: '/purchases', icon: Package, label: 'Purchases', module: 'purchases' },
  { path: '/production', icon: Factory, label: 'Production', module: 'production' },
  { path: '/orders', icon: ShoppingCart, label: 'Orders', module: 'orders' },
  { path: '/expenses', icon: DollarSign, label: 'Expenses', module: 'expenses' },
  { path: '/inventory', icon: Package, label: 'Inventory', module: 'inventory' },
  { path: '/commodity-inventory', icon: Boxes, label: 'Commodity Stock', module: 'inventory' },
  { path: '/daily-close', icon: ClipboardCheck, label: 'Daily Close', module: 'daily_close' },
  { path: '/production-costing', icon: Calculator, label: 'Production Costing', module: 'production_costing' },
  { path: '/reports', icon: PieChart, label: 'Reports', module: 'reports' },
  { path: '/settings', icon: Settings, label: 'Settings', module: 'settings' },
];

const adminItems = [
  { path: '/users', icon: UserCog, label: 'Users', module: 'users' },
  { path: '/audit-log', icon: History, label: 'Audit Log', module: 'audit_log' },
];

export default function Layout({ children }) {
  const { user, logout, isAdmin, hasModule } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Filter nav items by user permissions
  const visibleNavItems = navItems.filter(item => hasModule(item.module));
  const visibleAdminItems = adminItems.filter(item => hasModule(item.module));
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Desktop sidebar collapsed state (stored in localStorage)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved === 'true';
  });

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', sidebarCollapsed);
  }, [sidebarCollapsed]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const NavContent = ({ isMobile = false }) => (
    <>
      {/* Logo */}
      <div className={`p-4 border-b border-white/10 ${sidebarCollapsed && !isMobile ? 'px-2' : 'p-6'}`}>
        <div className={`flex items-center ${sidebarCollapsed && !isMobile ? 'justify-center' : 'gap-3'}`}>
          <img src={LOGO_URL} alt="MIALE" className={`${sidebarCollapsed && !isMobile ? 'h-8' : 'h-12'} w-auto`} />
          {(!sidebarCollapsed || isMobile) && (
            <div>
              <h1 className="font-heading font-bold text-lg">Primefield OS</h1>
              <p className="text-xs text-white/60">Mill Management</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 overflow-y-auto">
        <ul className="space-y-1">
          {visibleNavItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-white/20 text-white font-medium' 
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  } ${sidebarCollapsed && !isMobile ? 'justify-center px-2' : ''}`
                }
                title={sidebarCollapsed && !isMobile ? item.label : undefined}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {(!sidebarCollapsed || isMobile) && <span>{item.label}</span>}
              </NavLink>
            </li>
          ))}
        </ul>

        {visibleAdminItems.length > 0 && (
          <>
            <div className="my-4 border-t border-white/10" />
            {(!sidebarCollapsed || isMobile) && (
              <p className="px-4 text-xs text-white/40 uppercase tracking-wider mb-2">Admin</p>
            )}
            <ul className="space-y-1">
              {visibleAdminItems.map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                        isActive 
                          ? 'bg-white/20 text-white font-medium' 
                          : 'text-white/70 hover:bg-white/10 hover:text-white'
                      } ${sidebarCollapsed && !isMobile ? 'justify-center px-2' : ''}`
                    }
                    title={sidebarCollapsed && !isMobile ? item.label : undefined}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    {(!sidebarCollapsed || isMobile) && <span>{item.label}</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </>
        )}
      </nav>

      {/* User info */}
      <div className={`p-4 border-t border-white/10 ${sidebarCollapsed && !isMobile ? 'p-2' : ''}`}>
        {(!sidebarCollapsed || isMobile) && (
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <span className="font-heading font-bold">{user?.name?.charAt(0) || 'U'}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{user?.name}</p>
              <p className="text-xs text-white/60 truncate">{user?.email}</p>
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors ${
            sidebarCollapsed && !isMobile ? 'px-2' : ''
          }`}
          title={sidebarCollapsed && !isMobile ? 'Logout' : undefined}
          data-testid="logout-btn"
        >
          <LogOut className="h-4 w-4" />
          {(!sidebarCollapsed || isMobile) && <span>Logout</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-[#F9FAF9]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#1B4D3E] text-white px-4 py-3 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="MIALE" className="h-8 w-auto" />
          <span className="font-heading font-bold">Primefield OS</span>
        </div>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/50" 
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside 
        className={`lg:hidden fixed top-0 left-0 h-full z-50 w-72 bg-gradient-to-b from-[#1B4D3E] to-[#0f2e24] text-white flex flex-col transform transition-transform duration-300 ease-in-out ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <NavContent isMobile={true} />
      </aside>

      {/* Desktop Sidebar */}
      <aside 
        className={`hidden lg:flex fixed h-full z-30 bg-gradient-to-b from-[#1B4D3E] to-[#0f2e24] text-white flex-col transition-all duration-300 ${
          sidebarCollapsed ? 'w-16' : 'w-64'
        }`}
      >
        <NavContent isMobile={false} />
        
        {/* Desktop Collapse Toggle */}
        <button
          onClick={toggleSidebar}
          className="absolute -right-3 top-20 bg-[#1B4D3E] text-white p-1.5 rounded-full shadow-lg hover:bg-[#0f2e24] transition-colors border-2 border-white/20"
          data-testid="sidebar-toggle"
        >
          {sidebarCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      </aside>

      {/* Main content */}
      <main 
        className={`flex-1 min-w-0 transition-all duration-300 ${
          sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        } pt-14 lg:pt-0`}
      >
        <div className="p-3 sm:p-4 md:p-6 lg:p-8 overflow-x-hidden">
          {children}
        </div>
      </main>
    </div>
  );
}
